import { useState, useCallback, useMemo } from 'react';
import type { Market } from '@paulownia/shared/types';
import type { SlugStatus } from './useSlugValidation';

/**
 * Onboarding wizard steps
 */
export const ONBOARDING_STEPS = [
  { id: 'tenant-info', label: 'Mon commerce' },
  { id: 'markets', label: 'Mes marchés' },
  { id: 'confirmation', label: 'Confirmation' },
] as const;

export type OnboardingStepId = (typeof ONBOARDING_STEPS)[number]['id'];

/**
 * Form data for step 1: Tenant information
 */
export interface TenantInfoFormData {
  name: string;
  slug: string;
  description?: string;
  phoneNumber?: string;
  openingHours?: string;
}

/**
 * Selected market with tenant-specific settings
 */
export interface SelectedMarket {
  marketId: string;
  market: Market;
  orderStartTime?: string;
  orderEndTime?: string;
}

/**
 * Form data for step 2: Market selection
 */
export interface MarketSelectionFormData {
  selectedMarkets: SelectedMarket[];
}

/**
 * Complete onboarding form data
 */
export interface OnboardingFormData {
  tenantInfo: TenantInfoFormData;
  marketSelection: MarketSelectionFormData;
}

// Re-export SlugStatus from useSlugValidation for backward compatibility
export type { SlugStatus };

/**
 * Hook state interface
 */
export interface OnboardingState {
  currentStep: number;
  formData: OnboardingFormData;
  slugStatus: SlugStatus;
  isSubmitting: boolean;
}

const INITIAL_FORM_DATA: OnboardingFormData = {
  tenantInfo: {
    name: '',
    slug: '',
    description: undefined,
    phoneNumber: undefined,
    openingHours: undefined,
  },
  marketSelection: {
    selectedMarkets: [],
  },
};

const MAX_MARKETS_FREE_TIER = 3;

/**
 * Hook for managing onboarding wizard state and navigation
 */
export function useOnboardingState() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<OnboardingFormData>(INITIAL_FORM_DATA);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step validation
  const isStep1Valid = useMemo(() => {
    const { name, slug } = formData.tenantInfo;
    return (
      name.trim().length >= 2 &&
      name.trim().length <= 100 &&
      slug.trim().length >= 3 &&
      slugStatus === 'available'
    );
  }, [formData.tenantInfo, slugStatus]);

  const isStep2Valid = useMemo(() => {
    return formData.marketSelection.selectedMarkets.length >= 1;
  }, [formData.marketSelection]);

  const isStep3Valid = useMemo(() => {
    // Confirmation step is valid if steps 1 and 2 are valid
    return isStep1Valid && isStep2Valid;
  }, [isStep1Valid, isStep2Valid]);

  const isCurrentStepValid = useMemo(() => {
    switch (currentStep) {
      case 0:
        return isStep1Valid;
      case 1:
        return isStep2Valid;
      case 2:
        return isStep3Valid;
      default:
        return false;
    }
  }, [currentStep, isStep1Valid, isStep2Valid, isStep3Valid]);

  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

  // Navigation
  const goToNextStep = useCallback(() => {
    if (currentStep < ONBOARDING_STEPS.length - 1 && isCurrentStepValid) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep, isCurrentStepValid]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      // Only allow going back to previous steps or current step
      if (stepIndex <= currentStep && stepIndex >= 0 && stepIndex < ONBOARDING_STEPS.length) {
        setCurrentStep(stepIndex);
      }
    },
    [currentStep]
  );

  // Form data updates
  const updateTenantInfo = useCallback((updates: Partial<TenantInfoFormData>) => {
    setFormData((prev) => ({
      ...prev,
      tenantInfo: {
        ...prev.tenantInfo,
        ...updates,
      },
    }));
  }, []);

  const updateMarketSelection = useCallback((updates: Partial<MarketSelectionFormData>) => {
    setFormData((prev) => ({
      ...prev,
      marketSelection: {
        ...prev.marketSelection,
        ...updates,
      },
    }));
  }, []);

  // Market selection helpers
  const addMarket = useCallback((market: Market) => {
    setFormData((prev) => {
      const isAlreadySelected = prev.marketSelection.selectedMarkets.some(
        (m) => m.marketId === market.id
      );
      if (isAlreadySelected) return prev;

      if (prev.marketSelection.selectedMarkets.length >= MAX_MARKETS_FREE_TIER) {
        return prev; // Limit reached
      }

      return {
        ...prev,
        marketSelection: {
          ...prev.marketSelection,
          selectedMarkets: [
            ...prev.marketSelection.selectedMarkets,
            { marketId: market.id, market },
          ],
        },
      };
    });
  }, []);

  const removeMarket = useCallback((marketId: string) => {
    setFormData((prev) => ({
      ...prev,
      marketSelection: {
        ...prev.marketSelection,
        selectedMarkets: prev.marketSelection.selectedMarkets.filter(
          (m) => m.marketId !== marketId
        ),
      },
    }));
  }, []);

  const isMarketSelected = useCallback(
    (marketId: string) => {
      return formData.marketSelection.selectedMarkets.some((m) => m.marketId === marketId);
    },
    [formData.marketSelection.selectedMarkets]
  );

  const canSelectMoreMarkets = useMemo(() => {
    return formData.marketSelection.selectedMarkets.length < MAX_MARKETS_FREE_TIER;
  }, [formData.marketSelection.selectedMarkets.length]);

  // Reset form
  const resetForm = useCallback(() => {
    setCurrentStep(0);
    setFormData(INITIAL_FORM_DATA);
    setSlugStatus('idle');
    setIsSubmitting(false);
  }, []);

  return {
    // State
    currentStep,
    formData,
    slugStatus,
    isSubmitting,

    // Validation
    isCurrentStepValid,
    isStep1Valid,
    isStep2Valid,
    isStep3Valid,
    isLastStep,

    // Navigation
    goToNextStep,
    goToPreviousStep,
    goToStep,

    // Form updates
    updateTenantInfo,
    updateMarketSelection,
    setSlugStatus,
    setIsSubmitting,

    // Market helpers
    addMarket,
    removeMarket,
    isMarketSelected,
    canSelectMoreMarkets,
    maxMarkets: MAX_MARKETS_FREE_TIER,

    // Reset
    resetForm,

    // Constants
    steps: ONBOARDING_STEPS,
  };
}
